import React, { useState } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { DatePicker, Select } from 'antd';

import AutoCompleteComponent from '../Compare/AutoComplete';
import IndustryDropdown from '../Industries/Dropdown.component';
import { ColoredLabel } from '../../Screens/Industries/Industries.desktop.screen';
import { RightTabContainer, RightTabItem } from '../../Screens/SingleReport/SingleReport.styled';
import { CALL, DATE_NOW, REPORT_DATE_RANGE } from '../../Constants';

const { RangePicker } = DatePicker;

const ModeGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px;
`;

const ModeButton = styled.button<{ selected: boolean }>`
	border: 1px solid ${({ selected }) => (selected ? '#225fb0' : '#dee2e6')};
	border-radius: 4px;
	background: ${({ selected }) => (selected ? '#e0ecfc' : '#fff')};
	color: ${({ selected }) => (selected ? '#1f78b4' : '#4e4e4e')};
	padding: 8px;
	text-align: left;
	cursor: pointer;
`;

const HelperText = styled.div`
	color: #6c757d;
	font-size: 10px;
	font-weight: 500;
`;

const Section = styled.div`
	border-bottom: 1px solid #e0e0e0;
	padding: 8px 0 16px;
`;

const SectionTitle = styled.div`
	color: #2d2d2d;
	font-size: 12px;
	font-weight: 700;
	margin-bottom: 8px;
`;

const SearchTypeButton = styled.button<{ selected: boolean }>`
	border: 1px solid ${({ selected }) => (selected ? '#225fb0' : '#dee2e6')};
	border-radius: 4px;
	background: ${({ selected }) => (selected ? '#e0ecfc' : '#fff')};
	color: ${({ selected }) => (selected ? '#1f78b4' : '#4e4e4e')};
	padding: 6px 10px;
	font-size: 10px;
	font-weight: 600;
	cursor: pointer;
`;

const ConfirmButton = styled.button`
	border: 0;
	border-radius: 4px;
	background: #225fb0;
	color: #fff;
	padding: 10px;
	width: 100%;
	font-size: 12px;
	font-weight: 600;
	cursor: pointer;
`;

const ButtonGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px;
`;

const RangeButton = styled.button<{ selected: boolean }>`
	border: 1px solid ${({ selected }) => (selected ? '#225fb0' : '#dee2e6')};
	border-radius: 4px;
	background: ${({ selected }) => (selected ? '#e0ecfc' : '#fff')};
	color: #2d2d2d;
	padding: 8px;
	font-size: 12px;
	font-weight: 600;
	cursor: pointer;
`;

interface IReportSearchPanel {
	searchMode: 'company' | 'industry';
	setSearchMode: (value: 'company' | 'industry') => void;
	industryCode: string[];
	setIndustryCode: React.Dispatch<React.SetStateAction<string[]>>;
	onSelectCompany: (value: any[]) => (id?: string) => void;
	onSelectIndustry: () => boolean;
	onApplyFilters: () => void;
	filtered: any[];
	onDeleteList: (value: number) => () => void;
	setStartDate: React.Dispatch<React.SetStateAction<any>>;
	setFinishDate: React.Dispatch<React.SetStateAction<any>>;
	setSelectedRange: React.Dispatch<React.SetStateAction<any>>;
	selectedRange: { label: string };
	documentType: { label: string; value: string };
	setDocumentType: React.Dispatch<React.SetStateAction<any>>;
	startDate: any;
	finishDate: any;
}

const getRangeDate = (rangeType: string, value?: any) => {
	if (rangeType === REPORT_DATE_RANGE.CUSTOM_DATE.label) {
		return [
			value[0].format('YYYY-MM-DD'),
			value[1].format('YYYY-MM-DD'),
		];
	}
	if (rangeType === REPORT_DATE_RANGE.SIX_MONTH.label) {
		return [
			DATE_NOW.clone().subtract(6, 'months').add(1, 'day').format('YYYY-MM-DD'),
			DATE_NOW.clone().format('YYYY-MM-DD'),
		];
	}
	if (rangeType === REPORT_DATE_RANGE.YEAR.label) {
		return [
			DATE_NOW.clone().subtract(1, 'years').add(1, 'day').format('YYYY-MM-DD'),
			DATE_NOW.clone().format('YYYY-MM-DD'),
		];
	}
	if (rangeType === REPORT_DATE_RANGE.THREE_YEAR.label) {
		return [
			DATE_NOW.clone().subtract(3, 'years').add(1, 'day').format('YYYY-MM-DD'),
			DATE_NOW.clone().format('YYYY-MM-DD'),
		];
	}
	if (rangeType === REPORT_DATE_RANGE.FIVE_YEAR.label) {
		return [
			DATE_NOW.clone().subtract(5, 'years').add(1, 'day').format('YYYY-MM-DD'),
			DATE_NOW.clone().format('YYYY-MM-DD'),
		];
	}
	if (rangeType === REPORT_DATE_RANGE.TEN_YEAR.label) {
		return [
			DATE_NOW.clone().subtract(10, 'years').add(1, 'day').format('YYYY-MM-DD'),
			DATE_NOW.clone().format('YYYY-MM-DD'),
		];
	}
	return [
		DATE_NOW.clone().subtract(50, 'years').add(1, 'day').format('YYYY-MM-DD'),
		DATE_NOW.clone().format('YYYY-MM-DD'),
	];
};

const ReportSearchPanel = ({
	searchMode,
	setSearchMode,
	industryCode,
	setIndustryCode,
	onSelectCompany,
	onSelectIndustry,
	onApplyFilters,
	filtered,
	onDeleteList,
	setStartDate,
	setFinishDate,
	setSelectedRange,
	selectedRange,
	documentType,
	setDocumentType,
	startDate,
	finishDate,
}: IReportSearchPanel) => {
	const [companySearchType, setCompanySearchType] = useState('stock_code');
	const buttonDateValue = Array.from(Object.values(REPORT_DATE_RANGE));

	const changeRangeDate = (range: any, value?: any) => () => {
		const [startDateGenerated, finishDateGenerated] = getRangeDate(
			range.label,
			value
		);
		setSelectedRange(range);
		setStartDate(startDateGenerated);
		setFinishDate(finishDateGenerated);
	};

	const changeMode = (mode: 'company' | 'industry') => () => {
		setSearchMode(mode);
	};

	const placeholder =
		companySearchType === 'stock_code'
			? 'Enter stock code, e.g. 600903'
			: 'Enter company name';

	return (
		<React.Fragment>
			<Section>
				<SectionTitle>Search Mode</SectionTitle>
				<ModeGrid>
					<ModeButton selected={searchMode === 'company'} onClick={changeMode('company')}>
						<div className="fw-700 fs-12">Company</div>
						<HelperText>Search reports by company</HelperText>
					</ModeButton>
					<ModeButton selected={searchMode === 'industry'} onClick={changeMode('industry')}>
						<div className="fw-700 fs-12">Industry</div>
						<HelperText>Filter reports by industry</HelperText>
					</ModeButton>
				</ModeGrid>
			</Section>

			{searchMode === 'company' && (
				<Section>
					<SectionTitle>Find Company</SectionTitle>
					<div className="d-flex gap-2 mb-2">
						<SearchTypeButton
							selected={companySearchType === 'stock_code'}
							onClick={() => setCompanySearchType('stock_code')}
						>
							Stock Code
						</SearchTypeButton>
						<SearchTypeButton
							selected={companySearchType === 'company_name'}
							onClick={() => setCompanySearchType('company_name')}
						>
							Company Name
						</SearchTypeButton>
					</div>
					<AutoCompleteComponent
						onClickConfirm={onSelectCompany}
						searchScope="company"
						placeholder={placeholder}
					/>
					<HelperText className="mt-2">
						Choose a company first. Industry filters will not be mixed with company search.
					</HelperText>
				</Section>
			)}

			{searchMode === 'industry' && (
				<Section>
					<SectionTitle>Choose Industry</SectionTitle>
					<div className="d-flex row gap-1 mb-2">
						<IndustryDropdown
							industryCode={industryCode}
							setIndustryCode={setIndustryCode}
						/>
					</div>
					<ConfirmButton onClick={onSelectIndustry}>Apply Industry Filter</ConfirmButton>
					<HelperText className="mt-2">
						This mode searches reports by industry only, without requiring a company.
					</HelperText>
				</Section>
			)}

			<Section>
				<SectionTitle>Report Type</SectionTitle>
				<Select
					labelInValue
					value={documentType}
					style={{ width: '100%' }}
					options={CALL}
					onSelect={(value: any) => {
						setDocumentType(value);
					}}
				/>
			</Section>

			<Section>
				<SectionTitle>Date Range</SectionTitle>
				<RangePicker
					className="w-100 mb-2"
					format={'YYYY/MM/DD'}
					bordered={false}
					onChange={(value: any) => {
						if (value?.length > 0) {
							changeRangeDate(REPORT_DATE_RANGE.CUSTOM_DATE, value)();
						}
					}}
					value={[
						startDate && dayjs(startDate),
						finishDate && dayjs(finishDate),
					]}
				/>
				<ButtonGrid>
					{buttonDateValue.map((item: any, index) => (
						<RangeButton
							key={index}
							selected={item === selectedRange}
							onClick={changeRangeDate(item)}
						>
							{item.label}
						</RangeButton>
					))}
				</ButtonGrid>
				<div className="mt-2">
					<ConfirmButton onClick={onApplyFilters}>Apply Filters</ConfirmButton>
				</div>
			</Section>

			<Section>
				<RightTabContainer>
					<RightTabItem onClick={() => {}} selected={true}>
						Active Filters
					</RightTabItem>
				</RightTabContainer>
				<div className="table-scrollable mb-2">
					{filtered?.length > 0 ? (
						filtered.map((code, index) => (
							<ColoredLabel
								key={index}
								sector={code[1]}
								className="text-white company-stock-code fw-600 fs-10 mx-1"
							>
								{searchMode === 'company' ? 'Company' : 'Industry'} {code[1]}
								<span
									className="ms-1 cursor-pointer"
									onClick={onDeleteList(index)}
								>
									x
								</span>
							</ColoredLabel>
						))
					) : (
						<HelperText>No company or industry selected yet.</HelperText>
					)}
				</div>
			</Section>
		</React.Fragment>
	);
};

export default ReportSearchPanel;
